<template>
  <div class="error-page">
    <div class="error-content">
      <div class="error-icon">
        <span class="icon-404">🔍</span>
      </div>
      
      <h1>Page Not Found</h1>
      <p class="error-message">
        Sorry, we couldn't find the page you're looking for. The page may have been moved, deleted, or you may have typed the wrong URL.
      </p>
      
      <div class="suggested-actions">
        <h2>What can you do?</h2>
        <div class="actions-grid">
          <NuxtLink to="/" class="action-card">
            <div class="action-icon">🏠</div>
            <h3>Go to Homepage</h3>
            <p>Start fresh with our main page and explore Tuck's budgeting features</p>
          </NuxtLink>
          
          <NuxtLink to="/wizard" class="action-card">
            <div class="action-icon">🧙‍♂️</div>
            <h3>Try Budget Wizard</h3>
            <p>Start your 11-step budget planning journey</p>
          </NuxtLink>
          
          <NuxtLink to="/guides" class="action-card">
            <div class="action-icon">📚</div>
            <h3>Learn with Guides</h3>
            <p>Watch our financial planning video tutorials</p>
          </NuxtLink>
          
          <NuxtLink to="/contact" class="action-card">
            <div class="action-icon">💬</div>
            <h3>Get Help</h3>
            <p>Contact our support team for assistance</p>
          </NuxtLink>
        </div>
      </div>
      
      <div class="popular-pages">
        <h2>Popular Pages</h2>
        <div class="page-links">
          <NuxtLink to="/debt-planner">Debt Planner</NuxtLink>
          <NuxtLink to="/emergency-fund">Emergency Fund Calculator</NuxtLink>
          <NuxtLink to="/investments">Investment Tracker</NuxtLink>
          <NuxtLink to="/deposit-tracker">House Deposit Tracker</NuxtLink>
          <NuxtLink to="/about">About Tuck</NuxtLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// SEO Configuration for 404 page
useSeoMeta({
  title: 'Page Not Found - Tuck Personal Budgeting App',
  description: 'The page you\'re looking for doesn\'t exist. Explore Tuck\'s UK personal budgeting tools, budget wizard, debt planner, and financial guides instead.',
  ogTitle: 'Page Not Found - Tuck Personal Budgeting App',
  ogDescription: 'Page not found. Explore Tuck\'s UK personal budgeting tools instead.',
  ogImage: '/marketing/tuck-og.png',
  robots: 'noindex, nofollow'  // 404 pages should not be indexed
});

// Set 404 status code
useHead({
  htmlAttrs: {
    'data-status': '404'
  }
});
</script>

<style scoped>
.error-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(135deg, var(--color-background) 0%, var(--color-primary-bg) 100%);
  font-family: 'Roboto', sans-serif;
}

.error-content {
  max-width: 800px;
  width: 100%;
  text-align: center;
  background: var(--color-surface);
  border-radius: 20px;
  padding: 3rem;
  box-shadow: var(--shadow-heavy);
}

.error-icon {
  margin-bottom: 2rem;
}

.icon-404 {
  font-size: 4rem;
  display: inline-block;
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

h1 {
  color: var(--color-text-dark);
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: 700;
}

.error-message {
  color: var(--color-text-secondary);
  font-size: 1.2rem;
  margin-bottom: 3rem;
  line-height: 1.6;
}

.suggested-actions h2 {
  color: var(--color-primary);
  font-size: 1.8rem;
  margin-bottom: 2rem;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
}

.action-card {
  background: var(--color-surface);
  border: 2px solid var(--color-border-light);
  border-radius: 12px;
  padding: 2rem 1.5rem;
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;
  text-align: center;
}

.action-card:hover {
  border-color: var(--color-primary);
  transform: translateY(-5px);
  box-shadow: var(--shadow-primary-hover);
}

.action-icon {
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.action-card h3 {
  color: var(--color-primary);
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
}

.action-card p {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  line-height: 1.4;
}

.popular-pages h2 {
  color: var(--color-primary);
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
}

.page-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
}

.page-links a {
  color: var(--color-primary);
  text-decoration: none;
  padding: 0.5rem 1rem;
  border: 1px solid var(--color-primary);
  border-radius: 25px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.page-links a:hover {
  background-color: var(--color-primary);
  color: var(--color-surface);
}

@media (max-width: 768px) {
  .error-content {
    padding: 2rem;
  }

  h1 {
    font-size: 2rem;
  }

  .actions-grid {
    grid-template-columns: 1fr;
  }

  .page-links {
    flex-direction: column;
    align-items: center;
  }

  .page-links a {
    width: 200px;
    text-align: center;
  }
}
</style>